import React, { useState } from 'react';
import { Mail, RotateCcw, CheckCircle, AlertCircle } from 'lucide-react';

const EmailVerificationHelper = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const handleResendVerification = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setResult({ type: 'error', message: 'Please enter your email address' });
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      // First register a user to trigger email verification
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: `user_${Date.now()}`,
          email: email,
          password: 'TempPassword123!',
          first_name: 'Demo',
          last_name: 'User'
        })
      });

      if (response.ok) {
        setResult({
          type: 'success',
          message: `Email verification sent to ${email}! Check the backend logs for the verification link.`
        });
      } else {
        const errorData = await response.json();
        setResult({
          type: 'error',
          message: errorData.detail || 'Failed to send verification email'
        });
      }
    } catch (error) {
      setResult({
        type: 'error',
        message: 'Network error occurred'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg border">
      <div className="text-center mb-6">
        <Mail className="w-12 h-12 text-videbits-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900">Email Verification Demo</h2>
        <p className="text-gray-600 mt-2">
          Send a verification email and see it in the backend logs
        </p>
      </div>

      <form onSubmit={handleResendVerification} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-videbits-500 transition-colors"
            placeholder="Enter your email"
            required
          />
        </div>

        {result && (
          <div className={`p-4 rounded-lg flex items-center space-x-2 ${
            result.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {result.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <p className="text-sm">{result.message}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-videbits-600 text-white py-3 px-4 rounded-lg hover:bg-videbits-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center space-x-2"
        >
          {isLoading ? (
            <RotateCcw className="w-5 h-5 animate-spin" />
          ) : (
            <Mail className="w-5 h-5" />
          )}
          <span>{isLoading ? 'Sending...' : 'Send Verification Email'}</span>
        </button>
      </form>

      <div className="mt-6 p-4 bg-videbits-50 border border-videbits-200 rounded-lg">
        <h3 className="font-semibold text-videbits-900 mb-2">📋 How to See the Email:</h3>
        <div className="text-sm text-videbits-800 space-y-1">
          <p><strong>1. Terminal Command:</strong></p>
          <code className="block bg-videbits-100 p-2 rounded text-xs">
            docker-compose logs -f backend | grep "EMAIL VERIFICATION"
          </code>
          <p className="mt-2"><strong>2. Look for:</strong></p>
          <p className="text-xs bg-videbits-100 p-2 rounded">
            🔗 DEVELOPMENT VERIFICATION LINK: http://localhost:3000/verify-email?token=...
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationHelper;